import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getProject, getScreens, getHotspots, BASE_URL } from "../api";

// ── Victory effect components ───────────────────────────────────────────────
function Confetti() {
  const colours = ["#f59e0b","#fbbf24","#4f46e5","#22c55e","#ef4444","#06b6d4","#ec4899"];
  const pieces = Array.from({ length: 120 });
  return (
    <div style={styles.effectContainer}>
      {pieces.map((_, i) => {
        const colour = colours[i % colours.length];
        const left = Math.random() * 100;
        const delay = Math.random() * 2.5;
        const duration = 2.5 + Math.random() * 3;
        const size = 7 + Math.random() * 10;
        const shape = Math.random();
        return (
          <div key={i} style={{
            position: "absolute", left: `${left}%`, top: "-20px",
            width: size, height: shape > 0.66 ? size * 0.4 : size,
            backgroundColor: colour,
            borderRadius: shape > 0.33 && shape <= 0.66 ? "50%" : "2px",
            animation: `fall ${duration}s ${delay}s ease-in forwards`, opacity: 0,
          }} />
        );
      })}
    </div>
  );
}

function Fireworks() {
  const colours = ["#f59e0b","#ef4444","#4f46e5","#22c55e","#06b6d4","#ec4899","#fbbf24","#8b5cf6"];
  const bursts = Array.from({ length: 24 });
  return (
    <div style={styles.effectContainer}>
      {bursts.map((_, i) => {
        const colour  = colours[i % colours.length];
        const left    = 5 + Math.random() * 90;
        const top     = 5 + Math.random() * 65;
        const delay   = Math.random() * 3.5;
        const size    = 40 + Math.random() * 70;
        return (
          <div key={i} style={{
            position: "absolute", left: `${left}%`, top: `${top}%`,
            width: size, height: size, borderRadius: "50%",
            border: `3px solid ${colour}`,
            boxShadow: `0 0 10px ${colour}, 0 0 20px ${colour}`,
            animation: `firework-expand 1.4s ${delay}s ease-out forwards`, opacity: 0,
          }} />
        );
      })}
    </div>
  );
}

function Balloons() {
  const colours = ["#ef4444","#f59e0b","#22c55e","#4f46e5","#ec4899","#06b6d4","#8b5cf6","#fbbf24"];
  const balloons = Array.from({ length: 18 });
  return (
    <div style={styles.effectContainer}>
      {balloons.map((_, i) => {
        const colour   = colours[i % colours.length];
        const left     = 3 + Math.random() * 94;
        const delay    = Math.random() * 3;
        const duration = 5 + Math.random() * 4;
        const size     = 32 + Math.random() * 28;
        return (
          <div key={i} style={{
            position: "absolute", left: `${left}%`, bottom: "-80px",
            width: size, height: size * 1.25,
            backgroundColor: colour, opacity: 0.88,
            borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
            animation: `balloon-rise ${duration}s ${delay}s ease-in-out forwards`,
          }} />
        );
      })}
    </div>
  );
}

function Stars() {
  const colours = ["#f59e0b","#fbbf24","#fcd34d","#fde68a","#ffffff","#f97316"];
  const stars = Array.from({ length: 90 });
  return (
    <div style={styles.effectContainer}>
      {stars.map((_, i) => {
        const colour   = colours[i % colours.length];
        const left     = Math.random() * 100;
        const delay    = Math.random() * 3;
        const duration = 2.5 + Math.random() * 3;
        const size     = 14 + Math.random() * 22;
        return (
          <div key={i} style={{
            position: "absolute", left: `${left}%`, top: "-30px",
            fontSize: size, lineHeight: 1, color: colour,
            animation: `star-fall ${duration}s ${delay}s ease-in forwards`, opacity: 0,
          }}>★</div>
        );
      })}
    </div>
  );
}

function VictoryEffect({ type }) {
  if (type === "fireworks") return <Fireworks />;
  if (type === "balloons")  return <Balloons />;
  if (type === "stars")     return <Stars />;
  if (type === "none")      return null;
  return <Confetti />;
}

// ── Encouraging messages shown on completion ────────────────────────────────
const COMPLETION_MESSAGES = [
  {
    emoji: "🏆",
    title: "Outstanding!",
    body: "You nailed it — that's exactly how it's done!",
  },
  {
    emoji: "🎯",
    title: "Spot on!",
    body: "Perfect execution from start to finish.",
  },
  {
    emoji: "🚀",
    title: "You smashed it!",
    body: "Every step was spot on. You're ready for the real thing!",
  },
  {
    emoji: "⭐",
    title: "Brilliant work!",
    body: "You've completed the simulation with flying colours!",
  },
  {
    emoji: "🎉",
    title: "Well done!",
    body: "You've completed the simulation successfully!",
  },
];

function shapeBorderRadius(shape, radius = 12) {
  if (shape === "circle") return "50%";
  if (shape === "rounded") return radius;
  return 3;
}

// ── Input hotspot component ─────────────────────────────────────────────────
function InputHotspot({ hotspot, scaledStyle, onSuccess, onFail, locked }) {
  const [value, setValue] = useState("");
  const [status, setStatus] = useState("idle"); // idle | success | fail

  const validate = useCallback(() => {
    if (locked || status === "success") return;
    if (value.trim() === "") return;
    const expected = (hotspot.correct_answer || "")
      .toString()
      .trim()
      .toLowerCase();
    const entered = value.trim().toLowerCase();
    if (entered === expected) {
      setStatus("success");
      onSuccess(hotspot);
    } else {
      setStatus("fail");
      onFail();
      setTimeout(() => setStatus("idle"), 1800);
    }
  }, [value, hotspot, onSuccess, onFail, locked, status]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") validate();
    e.stopPropagation();
  };

  const borderColour = locked
    ? "rgba(255,255,255,0.15)"
    : status === "success"
      ? "#22c55e"
      : status === "fail"
        ? "#ef4444"
        : "rgba(245,158,11,0.7)";

  return (
    <div
      style={{
        ...styles.hotspotBase,
        ...scaledStyle,
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "0 4px",
        backgroundColor: locked ? "transparent" : "rgba(255,255,255,0.92)",
        border: locked ? "none" : `2px solid ${borderColour}`,
        pointerEvents: locked ? "none" : "auto",
        borderRadius: shapeBorderRadius(hotspot.shape, hotspot.border_radius ?? 12),
        boxSizing: "border-box",
        transition: "border-color 0.2s, background-color 0.2s",
        cursor: locked ? "not-allowed" : "text",
      }}
      onClick={(e) => e.stopPropagation()}
      title={locked ? "Complete the previous steps first" : undefined}
    >
      {!locked && (
        <>
          <input
            type={hotspot.type === "number" ? "number" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={validate}
            disabled={status === "success"}
            placeholder="…"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: Math.max(12, (scaledStyle.height || 28) * 0.55),
              color: "#1a1a2e",
              minWidth: 0,
              cursor: "text",
            }}
            aria-label={hotspot.tooltip_text || "Enter your answer"}
          />

          {status === "success" && (
            <span style={{ color: "#22c55e", fontSize: 16, flexShrink: 0 }}>
              ✓
            </span>
          )}
        </>
      )}
    </div>
  );
}

// ── Tooltip banner — shows all hotspot instructions with strikethrough ──────
function TooltipBanner({
  hotspots,
  completedIds,
  lockedIds,
  feedbackMessage,
  feedbackType,
}) {
  if (feedbackMessage) {
    return (
      <div
        style={{
          ...styles.tooltipBanner,
          backgroundColor: feedbackType === "success" ? "#166534" : "#7f1d1d",
          transition: "background-color 0.3s ease",
        }}
        role="status"
        aria-live="polite"
      >
        {feedbackType === "success" ? "✅" : "❌"} {feedbackMessage}
      </div>
    );
  }

  // Collapse alternatives into a single task per step number
  const stepMap = new Map();
  hotspots.forEach((h) => {
    const s = h.step_number ?? 1;
    if (!stepMap.has(s)) stepMap.set(s, { step: s, tooltip: "", ids: [] });
    const entry = stepMap.get(s);
    entry.ids.push(h.id);
    if (!entry.tooltip && h.tooltip_text) entry.tooltip = h.tooltip_text;
  });
  const steps = [...stepMap.values()]
    .filter((e) => e.tooltip)
    .sort((a, b) => a.step - b.step);
  if (steps.length === 0) return null;

  const isDone = (e) => e.ids.some((id) => completedIds.has(id));
  const isLocked = (e) => e.ids.every((id) => lockedIds.has(id));

  return (
    <div style={styles.tooltipBanner} role="status" aria-live="polite">
      {steps.length === 1 ? (
        <span
          style={{
            textDecoration: isDone(steps[0]) ? "line-through" : "none",
            opacity: isDone(steps[0]) ? 0.5 : 1,
            transition: "opacity 0.3s",
          }}
        >
          💡 {steps[0].tooltip}
        </span>
      ) : (
        <div style={styles.tooltipList}>
          {steps.map((e, i) => {
            const done = isDone(e);
            const locked = isLocked(e);
            return (
              <span key={e.step} style={styles.tooltipItem}>
                <span
                  style={{
                    ...styles.tooltipNumber,
                    backgroundColor: done
                      ? "#22c55e"
                      : locked
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(245,158,11,0.25)",
                    color: done ? "white" : locked ? "#555" : "#f59e0b",
                    transition: "background-color 0.3s, color 0.3s",
                  }}
                >
                  {done ? "✓" : locked ? "🔒" : i + 1}
                </span>
                <span
                  style={{
                    textDecoration: done ? "line-through" : "none",
                    opacity: done ? 0.45 : locked ? 0.35 : 1,
                    transition: "opacity 0.3s",
                  }}
                >
                  {e.tooltip}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Preview component ──────────────────────────────────────────────────
export default function Preview() {
  const navigate = useNavigate();
  const projectId = window.location.pathname.split("/")[2];
  const [project, setProject] = useState(null);
  const [screens, setScreens] = useState([]);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [hotspots, setHotspots] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackType, setFeedbackType] = useState("");
  const [completed, setCompleted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEffect, setShowEffect] = useState(false);
  const [allHotspots, setAllHotspots] = useState({});
  const [completionMsg] = useState(
    () =>
      COMPLETION_MESSAGES[
        Math.floor(Math.random() * COMPLETION_MESSAGES.length)
      ],
  );
  const [completedIds, setCompletedIds] = useState(new Set());

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const fullscreenBtnRef = useRef(null);

  // ── Derive which hotspots are currently locked ──────────────────────────
  // A hotspot is locked if it has requires_previous=true AND any earlier
  // hotspot (by position in the array) has not yet been completed.
  const getLockedIds = useCallback((currentHotspots, currentCompletedIds) => {
    // A step is complete when any hotspot with that step number is completed
    // (hotspots sharing a step number are alternatives).
    const completedSteps = new Set();
    currentHotspots.forEach((h) => {
      if (currentCompletedIds.has(h.id)) completedSteps.add(h.step_number ?? 1);
    });
    const locked = new Set();
    for (const h of currentHotspots) {
      if (!h.requires_previous) continue;
      const myStep = h.step_number ?? 1;
      // Locked until every earlier step is complete
      const earlierIncomplete = currentHotspots.some(
        (o) => (o.step_number ?? 1) < myStep && !completedSteps.has(o.step_number ?? 1),
      );
      if (earlierIncomplete) locked.add(h.id);
    }
    return locked;
  }, []);

  // ── Data fetching ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (screens.length > 0) {
      const current = screens[currentScreenIndex];
      if (current && allHotspots[current.id]) {
        setHotspots(allHotspots[current.id]);
      }
      setFeedbackMessage("");
      setFeedbackType("");
      setCompletedIds(new Set());
    }
  }, [currentScreenIndex, screens, allHotspots]);

  // ── Fullscreen change listener ──────────────────────────────────────────
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // ── Inject keyframe animations once, clean up on unmount ───────────────
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.id = "preview-keyframes";
    styleSheet.textContent = `
      @keyframes fall {
        0%   { transform: translateY(0)     rotate(0deg);   opacity: 1; }
        100% { transform: translateY(105vh) rotate(800deg); opacity: 0; }
      }
      @keyframes firework-expand {
        0%   { transform: scale(0);   opacity: 1; }
        60%  { opacity: 1; }
        100% { transform: scale(2.8); opacity: 0; }
      }
      @keyframes balloon-rise {
        0%   { transform: translateY(0)      rotate(-4deg); opacity: 0.9; }
        25%  { transform: translateY(-28vh)  rotate(4deg);  opacity: 0.9; }
        50%  { transform: translateY(-55vh)  rotate(-4deg); opacity: 0.85; }
        75%  { transform: translateY(-82vh)  rotate(4deg);  opacity: 0.7; }
        100% { transform: translateY(-115vh) rotate(-4deg); opacity: 0; }
      }
      @keyframes star-fall {
        0%   { transform: translateY(0)      rotate(0deg);   opacity: 1; }
        100% { transform: translateY(105vh)  rotate(720deg); opacity: 0; }
      }
      @keyframes popIn {
        0%   { transform: scale(0.7); opacity: 0; }
        100% { transform: scale(1);   opacity: 1; }
      }
      *:focus-visible {
        outline: 3px solid #f59e0b !important;
        outline-offset: 3px !important;
        border-radius: 4px;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  const fetchData = async () => {
    try {
      const { data: proj } = await getProject(projectId);
      setProject(proj);
      const { data: scr } = await getScreens(projectId);
      setScreens(scr);
      const hotspotsResults = await Promise.all(scr.map((s) => getHotspots(s.id)));
      const hotspotsMap = {};
      scr.forEach((s, i) => { hotspotsMap[s.id] = hotspotsResults[i].data; });
      setAllHotspots(hotspotsMap);
      if (scr.length > 0) setHotspots(hotspotsMap[scr[0].id] || []);
    } catch (err) {
      console.error("Could not load preview data");
    }
  };

  // ── Image scaling ───────────────────────────────────────────────────────
  // getBoundingClientRect() returns the element box, which may be larger than
  // the visible image when objectFit:contain adds letterbox padding. We
  // calculate the actual rendered content size and offset ourselves.
  // Coordinates are fractions (0-1) of the image, so positions are pure CSS
  // percentages relative to the image container. No measurement, so they stay
  // correct at any screen size and regardless of image load timing.
  const getScaledStyle = useCallback(
    (hotspot) => ({
      left: `${hotspot.x * 100}%`,
      top: `${hotspot.y * 100}%`,
      width: `${hotspot.width * 100}%`,
      height: `${hotspot.height * 100}%`,
    }),
    [],
  );

  // ── Advance screen / completion logic ───────────────────────────────────
  const advanceOrComplete = useCallback(() => {
    const nextIndex = currentScreenIndex + 1;
    if (nextIndex < screens.length) {
      setCurrentScreenIndex(nextIndex);
      const nextScreen = screens[nextIndex];
      const nextHotspots = allHotspots[nextScreen.id] || [];
      if (nextHotspots.length === 0) {
        setTimeout(() => {
          setCompleted(true);
          if (project?.victory_effect !== 'none') setShowEffect(true);
          if (project?.redirect_url) {
            setTimeout(() => { window.location.href = project.redirect_url; }, 4000);
          }
        }, 1200);
      }
    } else {
      setTimeout(() => {
        setCompleted(true);
        if (project?.victory_effect !== 'none') setShowEffect(true);
        if (project?.redirect_url) {
          setTimeout(() => { window.location.href = project.redirect_url; }, 4000);
        }
      }, 600);
    }
  }, [currentScreenIndex, screens, allHotspots, project]);

  // ── Unified hotspot completion ──────────────────────────────────────────
  const markCompleted = useCallback(
    (hotspot, successMsg) => {
      setFeedbackMessage(successMsg || "Correct!");
      setFeedbackType("success");

      setCompletedIds((prev) => {
        const next = new Set(prev).add(hotspot.id);
        // The screen is done when every step has at least one completed hotspot
        const doneSteps = new Set();
        hotspots.forEach((h) => {
          if (next.has(h.id)) doneSteps.add(h.step_number ?? 1);
        });
        const allSteps = new Set(hotspots.map((h) => h.step_number ?? 1));
        const allDone = [...allSteps].every((s) => doneSteps.has(s));
        if (allDone) {
          setTimeout(advanceOrComplete, 900);
        }
        return next;
      });

      setTimeout(() => {
        setFeedbackMessage("");
        setFeedbackType("");
      }, 1500);
    },
    [hotspots, advanceOrComplete],
  );

  // ── Wrong click ─────────────────────────────────────────────────────────
  const handleWrongClick = useCallback(() => {
    const failMsg = hotspots[0]?.fail_message || "Not quite — try again!";
    setFeedbackMessage(failMsg);
    setFeedbackType("fail");
    setTimeout(() => {
      setFeedbackMessage("");
      setFeedbackType("");
    }, 2000);
  }, [hotspots]);

  // ── Locked hotspot click — gentle reminder ──────────────────────────────
  const handleLockedClick = useCallback((e) => {
    e.stopPropagation();
    setFeedbackMessage("Complete the previous steps first!");
    setFeedbackType("fail");
    setTimeout(() => {
      setFeedbackMessage("");
      setFeedbackType("");
    }, 2000);
  }, []);

  // ── Hint when a right-click hotspot is left-clicked ─────────────────────
  const handleRightClickHint = useCallback(() => {
    setFeedbackMessage("Try right-clicking this one.");
    setFeedbackType("fail");
    setTimeout(() => {
      setFeedbackMessage("");
      setFeedbackType("");
    }, 2000);
  }, []);

  // ── Fullscreen toggle ───────────────────────────────────────────────────
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // ── Keyboard: Escape closes completion overlay ──────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && completed) {
        setCompleted(false);
        setShowEffect(false);
        setCurrentScreenIndex(0);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [completed]);

  const currentScreen = screens[currentScreenIndex];
  const clickHotspots = hotspots.filter((h) => h.type === "click");
  const inputHotspots = hotspots.filter(
    (h) => h.type === "text" || h.type === "number",
  );
  const lockedIds = getLockedIds(hotspots, completedIds);
  // Steps already satisfied — used to hide the not-chosen alternatives
  const completedSteps = new Set();
  hotspots.forEach((h) => {
    if (completedIds.has(h.id)) completedSteps.add(h.step_number ?? 1);
  });

  return (
    <div ref={containerRef} style={styles.container}>
      {/* ── Header ── */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            style={styles.backButton}
            onClick={() => (navigate(`/projects/${projectId}`))}
            aria-label="Back to project"
          >
            ← Back
          </button>
          <span style={styles.headerTitle}>
            {project?.title || "Preview"}
            <span style={styles.stepBadge}>
              Step {currentScreenIndex + 1} of {screens.length}
            </span>
          </span>
        </div>

        <button
          ref={fullscreenBtnRef}
          style={styles.fullscreenButton}
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
          title={isFullscreen ? "Exit full screen (Esc)" : "Full screen"}
        >
          {isFullscreen ? (
            <>{exitFullscreenIcon} Exit Full Screen</>
          ) : (
            <>{enterFullscreenIcon} Full Screen</>
          )}
        </button>
      </header>

      {/* ── Instruction / feedback banner + canvas ── */}
      <main id="main-content" style={{ display: 'contents' }}>
      {!completed && (
        <TooltipBanner
          hotspots={hotspots}
          completedIds={completedIds}
          lockedIds={lockedIds}
          feedbackMessage={feedbackMessage}
          feedbackType={feedbackType}
        />
      )}

      {/* ── Main canvas ── */}
      <div
        style={styles.canvas}
        onClick={handleWrongClick}
        onContextMenu={(e) => e.preventDefault()}
      >
        {currentScreen && (
          <div style={styles.imageContainer}>
            <img
              ref={imageRef}
              src={`${BASE_URL}/uploads/${currentScreen.image_path}`}
              alt={currentScreen.alt_text || `Screen ${currentScreenIndex + 1}`}
              style={styles.image}
              draggable={false}
            />

            {/* Click hotspots */}
            {!completed &&
              clickHotspots.map((hotspot) => {
                const done = completedIds.has(hotspot.id);
                const locked = lockedIds.has(hotspot.id);
                const step = hotspot.step_number ?? 1;
                // Hide the alternatives that weren't chosen once the step is done
                if (!done && completedSteps.has(step)) return null;
                const isRight = !!hotspot.right_click;
                return (
                  <div
                    key={hotspot.id}
                    role="button"
                    tabIndex={done || locked ? -1 : 0}
                    aria-label={
                      (hotspot.tooltip_text || "Click here") +
                      (isRight ? " (right-click)" : "")
                    }
                    aria-pressed={done}
                    aria-disabled={locked}
                    title={
                      locked
                        ? "Complete the previous steps first"
                        : isRight
                          ? "Right-click here"
                          : undefined
                    }
                    style={{
                      ...styles.hotspotBase,
                      ...getScaledStyle(hotspot),
                      borderRadius: shapeBorderRadius(hotspot.shape, hotspot.border_radius ?? 12),
                      cursor: done
                        ? "default"
                        : locked
                          ? "not-allowed"
                          : "pointer",
                      backgroundColor: done
                        ? "rgba(34,197,94,0.18)"
                        : "transparent",
                      border: done
                        ? "2px solid rgba(34,197,94,0.5)"
                        : "2px solid transparent",
                      transition: "background-color 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 16,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (locked) {
                        handleLockedClick(e);
                        return;
                      }
                      if (done) return;
                      if (isRight) {
                        handleRightClickHint();
                        return;
                      }
                      markCompleted(hotspot, hotspot.success_message);
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (locked) {
                        handleLockedClick(e);
                        return;
                      }
                      if (!done && isRight)
                        markCompleted(hotspot, hotspot.success_message);
                    }}
                    onKeyDown={(e) => {
                      if (
                        (e.key === "Enter" || e.key === " ") &&
                        !done &&
                        !locked
                      ) {
                        e.preventDefault();
                        markCompleted(hotspot, hotspot.success_message);
                      }
                    }}
                  ></div>
                );
              })}

            {/* Text / number input hotspots */}
            {!completed &&
              inputHotspots.map((hotspot) => (
                <InputHotspot
                  key={hotspot.id}
                  hotspot={hotspot}
                  scaledStyle={getScaledStyle(hotspot)}
                  onSuccess={(hs) => markCompleted(hs, hs.success_message)}
                  onFail={handleWrongClick}
                  locked={lockedIds.has(hotspot.id)}
                />
              ))}
          </div>
        )}
      </div>

      {/* ── Completion overlay ── */}
      {completed && (
        <div
          style={styles.completedOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Simulation complete"
        >
          {showEffect && <VictoryEffect type={project?.victory_effect || "confetti"} />}
          <div style={styles.completedBox}>
            <div style={styles.completedEmoji}>{completionMsg.emoji}</div>
            <h1 style={styles.completedTitle}>{project?.victory_title || completionMsg.title}</h1>
            <p style={styles.completedText}>{project?.victory_message || completionMsg.body}</p>
            <div style={styles.completedActions}>
              <button
                style={styles.restartButton}
                autoFocus
                onClick={() => {
                  setCurrentScreenIndex(0);
                  setCompleted(false);
                  setShowEffect(false);
                  setCompletedIds(new Set());
                }}
              >
                ↩ Restart
              </button>
              <button
                style={styles.backToDashboard}
                onClick={() =>
                  (navigate(`/projects/${projectId}`))
                }
              >
                Back to Project
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
    </div>
  );
}

// ── SVG icons for fullscreen button ────────────────────────────────────────
const enterFullscreenIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: 6, verticalAlign: "middle" }}
  >
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);
const exitFullscreenIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: 6, verticalAlign: "middle" }}
  >
    <polyline points="8 3 3 3 3 8" />
    <polyline points="21 8 21 3 16 3" />
    <polyline points="3 16 3 21 8 21" />
    <polyline points="16 21 21 21 21 16" />
  </svg>
);

// ── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  container: {
    height: "100vh",
    backgroundColor: "#111827",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    backgroundColor: "#1e3a5f",
    padding: "10px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
    flexShrink: 0,
    gap: 12,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
  },
  headerTitle: {
    color: "white",
    fontSize: 15,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 10,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  stepBadge: {
    backgroundColor: "rgba(79,70,229,0.35)",
    color: "#a5b4fc",
    fontSize: 12,
    fontWeight: 600,
    padding: "2px 10px",
    borderRadius: 20,
    border: "1px solid rgba(99,102,241,0.4)",
    letterSpacing: "0.03em",
  },
  backButton: {
    padding: "7px 14px",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#e2e8f0",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 7,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    flexShrink: 0,
    transition: "background-color 0.15s",
  },
  fullscreenButton: {
    display: "flex",
    alignItems: "center",
    padding: "7px 14px",
    backgroundColor: "#4f46e5",
    color: "white",
    border: "none",
    borderRadius: 7,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
    transition: "background-color 0.15s",
    whiteSpace: "nowrap",
  },
  tooltipBanner: {
    backgroundColor: "#1f2937",
    color: "white",
    padding: "16px 28px",
    fontSize: 18,
    borderBottom: "3px solid #f59e0b",
    textAlign: "center",
    flexShrink: 0,
    minHeight: 62,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.3s ease",
  },
  tooltipList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px 24px",
    justifyContent: "center",
    alignItems: "center",
  },
  tooltipItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    fontSize: 17,
  },
  tooltipNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "50%",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  canvas: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    overflow: "hidden",
    minHeight: 0,
  },
  imageContainer: {
    position: "relative",
    lineHeight: 0,
    maxWidth: "100%",
    maxHeight: "100%",
  },
  image: {
    display: "block",
    userSelect: "none",
    borderRadius: 4,
    maxWidth: "100%",
    maxHeight: "calc(100vh - 180px)",
  },
  hotspotBase: {
    position: "absolute",
    outline: "none",
    borderRadius: 3,
  },
  completedOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(10,10,30,0.88)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    padding: 20,
  },
  completedBox: {
    backgroundColor: "#1e1b4b",
    border: "2px solid #4f46e5",
    boxShadow: "0 0 60px rgba(79,70,229,0.4), 0 24px 48px rgba(0,0,0,0.6)",
    padding: "48px 56px",
    borderRadius: 20,
    textAlign: "center",
    maxWidth: 480,
    width: "100%",
    animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards",
  },
  completedEmoji: {
    fontSize: 64,
    marginBottom: 12,
    lineHeight: 1,
    filter: "drop-shadow(0 0 16px rgba(245,158,11,0.6))",
  },
  completedTitle: {
    margin: "0 0 12px 0",
    fontSize: 34,
    fontWeight: 800,
    color: "#f59e0b",
    letterSpacing: "-0.02em",
  },
  completedText: {
    margin: "0 0 36px 0",
    color: "#c7d2fe",
    fontSize: 17,
    lineHeight: 1.5,
  },
  completedActions: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  restartButton: {
    padding: "12px 28px",
    backgroundColor: "#4f46e5",
    color: "white",
    border: "none",
    borderRadius: 9,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background-color 0.15s",
  },
  backToDashboard: {
    padding: "12px 28px",
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "#e2e8f0",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 9,
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.15s",
  },
  effectContainer: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    overflow: "hidden",
    zIndex: 1000,
  },
};

